/**
 * AIProviders.tsx — US-243: Provider Settings UX and One-Time Key Entry (Epic 34)
 *
 * Replaces the existing OpenRouter-specific "AI Configuration" card in Settings.
 *
 * Features:
 * - Provider cards for OpenAI, Anthropic, OpenRouter, Custom.
 * - Enabled/disabled toggle per provider (calls PUT /api/provider-policies/:provider).
 * - Configured / Not Configured badge.
 * - One-time key entry: after save, clears input + shows "●●●● {hint}".
 * - "Test key" before saving calls POST /api/provider-credentials/validate.
 * - "Save key" calls POST /api/provider-credentials.
 * - Rotate / Revoke / Emergency disable buttons for saved keys.
 * - Shell-agnostic: works at 600-700px, dark-mode capable.
 *
 * Does NOT manage which model handles which task/tier — that's Settings →
 * Integrations → AI Configuration (`Settings.tsx`'s `TaskTierTable` and the
 * Fast/Standard/Premium pickers), which is real and wired to `system_config`.
 * A "Task Model Assignments" table used to live here too; removed 2026-09-25
 * because it was local-only state with no save path and modeled the wrong
 * abstraction (task → specific model instead of task → tier).
 *
 * Design: Direction B — Moderna (sky accent, warm stone neutrals).
 */
import { useState } from 'react';
import {
  Key,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  Plus,
} from 'lucide-react';
import {
  useProviderCredentials,
  useSaveCredential,
  useValidateCredential,
  useRevokeCredential,
  useRotateCredential,
  useProviderPolicy,
  useUpdateProviderPolicy,
} from '../../hooks/useAIProviders';
import type { ProviderCredentialMeta, ProviderPolicy } from '../../types';

// ---------------------------------------------------------------------------
// Provider display config
// ---------------------------------------------------------------------------

interface ProviderConfig {
  id: string;
  name: string;
  description: string;
  keyPlaceholder: string;
  docsUrl?: string;
}

const PROVIDER_CONFIGS: ProviderConfig[] = [
  {
    id: 'openrouter',
    name: 'OpenRouter',
    description: 'Route requests to any AI model through a single API.',
    keyPlaceholder: 'sk-or-v1-…',
    docsUrl: 'https://openrouter.ai/keys',
  },
  {
    id: 'openai',
    name: 'OpenAI',
    description: 'Direct access to GPT-4o, GPT-4o Mini, and embeddings.',
    keyPlaceholder: 'sk-…',
    docsUrl: 'https://platform.openai.com/api-keys',
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Direct access to Claude 3.5 Sonnet, Haiku, and Opus.',
    keyPlaceholder: 'sk-ant-…',
    docsUrl: 'https://console.anthropic.com/settings/keys',
  },
  {
    id: 'custom',
    name: 'Custom',
    description: 'OpenAI-compatible endpoint (self-hosted, Azure, etc.).',
    keyPlaceholder: 'Enter API key…',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function findCredential(
  credentials: ProviderCredentialMeta[],
  provider: string
): ProviderCredentialMeta | undefined {
  return credentials.find((c) => c.provider === provider && c.status === 'active');
}

function findPolicy(
  policies: ProviderPolicy[],
  provider: string
): ProviderPolicy | undefined {
  return policies.find((p) => p.provider === provider);
}

function isProviderEnabled(policies: ProviderPolicy[], provider: string): boolean {
  const policy = findPolicy(policies, provider);
  return policy ? policy.enabled : true; // default enabled if no explicit policy
}

// ---------------------------------------------------------------------------
// ProviderCard
// ---------------------------------------------------------------------------

interface ProviderCardProps {
  config: ProviderConfig;
  credential?: ProviderCredentialMeta;
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
  onSave: (secret: string) => Promise<void>;
  onTest: (secret: string) => Promise<{ valid: boolean; reason?: string }>;
  onRotate: () => Promise<void>;
  onRevoke: () => Promise<void>;
  isSaving: boolean;
  isTesting: boolean;
  isRevoking: boolean;
  isRotating: boolean;
}

function ProviderCard({
  config,
  credential,
  enabled,
  onToggleEnabled,
  onSave,
  onTest,
  onRotate,
  onRevoke,
  isSaving,
  isTesting,
  isRevoking,
  isRotating,
}: ProviderCardProps) {
  const [secretInput, setSecretInput] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; reason?: string } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedHint, setSavedHint] = useState<string | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  const isConfigured = !!credential && credential.status === 'active';
  const hasUnsavedKey = secretInput.trim().length > 0;

  const handleTest = async () => {
    if (!secretInput.trim()) return;
    setSaveError(null);
    setTestResult(null);
    try {
      const result = await onTest(secretInput.trim());
      setTestResult(result);
    } catch (err) {
      setTestResult({ valid: false, reason: err instanceof Error ? err.message : 'Test failed' });
    }
  };

  const handleSave = async () => {
    if (!secretInput.trim()) return;
    setSaveError(null);
    try {
      await onSave(secretInput.trim());
      // One-time key entry: clear input and show hint
      const hint = secretInput.length > 8
        ? `${secretInput.slice(0, 4)}${'●'.repeat(4)}${secretInput.slice(-4)}`
        : '●●●●';
      setSavedHint(hint);
      setSecretInput('');
      setTestResult(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save key');
    }
  };

  const handleToggle = async (newEnabled: boolean) => {
    setIsToggling(true);
    try {
      onToggleEnabled(newEnabled);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <div className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl p-4 space-y-4">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900 flex-shrink-0">
            <Key className="h-4 w-4 text-sky-600 dark:text-sky-400" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-stone-900 dark:text-stone-100">
              {config.name}
            </h3>
            <p className="text-xs text-stone-500 dark:text-stone-400 truncate">{config.description}</p>
          </div>
        </div>

        {/* Status badges + toggle */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isConfigured ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
              <CheckCircle className="h-3 w-3" />
              Configured
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400">
              Not configured
            </span>
          )}

          {/* Enable/disable toggle */}
          <label className="flex items-center cursor-pointer" aria-label={`${enabled ? 'Disable' : 'Enable'} ${config.name}`}>
            <input
              type="checkbox"
              role="switch"
              checked={enabled}
              disabled={isToggling}
              onChange={(e) => void handleToggle(e.target.checked)}
              className="sr-only"
            />
            <div
              onClick={() => !isToggling && void handleToggle(!enabled)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${
                enabled
                  ? 'bg-sky-500'
                  : 'bg-stone-300 dark:bg-stone-600'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </div>
          </label>
        </div>
      </div>

      {/* Saved key info */}
      {isConfigured && !hasUnsavedKey && (
        <div className="text-xs text-stone-500 dark:text-stone-400 space-y-1">
          <p className="font-mono">
            Key saved: {savedHint ?? credential.displayHint}
          </p>
          {credential.lastValidatedAt && (
            <p>Last validated: {new Date(credential.lastValidatedAt).toLocaleDateString()}</p>
          )}
          {credential.lastUsedAt && (
            <p>Last used: {new Date(credential.lastUsedAt).toLocaleDateString()}</p>
          )}
        </div>
      )}

      {/* Key entry (always shown for unconfigured, or to rotate) */}
      {(!isConfigured || hasUnsavedKey) && (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-stone-700 dark:text-stone-300">
            API Key {isConfigured ? '(enter to rotate)' : ''}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showSecret ? 'text' : 'password'}
                value={secretInput}
                onChange={(e) => {
                  setSecretInput(e.target.value);
                  setTestResult(null);
                  setSaveError(null);
                }}
                placeholder={config.keyPlaceholder}
                className="w-full rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 px-3 py-2 text-sm text-stone-900 dark:text-stone-100 pr-9 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:focus:ring-sky-800"
                autoComplete="off"
              />
              {hasUnsavedKey && (
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
                  tabIndex={-1}
                >
                  {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
          </div>

          {/* Test + Save buttons */}
          {hasUnsavedKey && (
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => void handleTest()}
                disabled={isTesting || !secretInput.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-xs font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTesting ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle className="h-3 w-3" />
                )}
                Test key
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={isSaving || !secretInput.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-xs font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <RefreshCw className="h-3 w-3 animate-spin" />
                ) : (
                  <Key className="h-3 w-3" />
                )}
                Save key
              </button>
            </div>
          )}

          {/* Test result */}
          {testResult && (
            <p className={`text-xs flex items-center gap-1 ${testResult.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {testResult.valid ? (
                <CheckCircle className="h-3.5 w-3.5" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5" />
              )}
              {testResult.valid ? 'Key is valid' : (testResult.reason ?? 'Invalid key')}
            </p>
          )}

          {/* Save error */}
          {saveError && (
            <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />
              {saveError}
            </p>
          )}
        </div>
      )}

      {/* Actions for configured providers */}
      {isConfigured && (
        <div className="flex gap-2 flex-wrap pt-1 border-t border-stone-100 dark:border-stone-800">
          <button
            type="button"
            onClick={() => void onRotate()}
            disabled={isRotating}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-800 text-xs font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700 disabled:opacity-50"
          >
            {isRotating ? (
              <RefreshCw className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
            Rotate
          </button>
          <button
            type="button"
            onClick={() => void onRevoke()}
            disabled={isRevoking}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-stone-800 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          >
            {isRevoking ? (
              <RefreshCw className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            Revoke
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state banner
// ---------------------------------------------------------------------------

function EmptyStateBanner() {
  return (
    <div className="rounded-xl border-2 border-dashed border-stone-200 dark:border-stone-700 p-6 text-center space-y-2">
      <div className="flex justify-center">
        <Plus className="h-8 w-8 text-stone-300 dark:text-stone-600" />
      </div>
      <p className="text-sm font-medium text-stone-600 dark:text-stone-400">
        Get started by adding a key for at least one provider
      </p>
      <p className="text-xs text-stone-400 dark:text-stone-500">
        Add an API key below to enable AI model access.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main AIProviders component
// ---------------------------------------------------------------------------

/**
 * AIProviders — shell-agnostic settings surface for managing AI provider keys,
 * enable/disable toggles, and task-to-model mappings.
 *
 * Works at 600-700px, dark-mode capable, no sidebar dependency.
 */
export default function AIProviders() {
  const { data: credData, isLoading: credsLoading } = useProviderCredentials();
  const { data: policyData } = useProviderPolicy();

  const saveCred = useSaveCredential();
  const validateCred = useValidateCredential();
  const revokeCred = useRevokeCredential();
  const rotateCred = useRotateCredential();
  const updatePolicy = useUpdateProviderPolicy();

  const credentials = credData?.credentials ?? [];
  const policies = policyData?.policies ?? [];

  const hasAnyConfigured = credentials.some((c) => c.status === 'active');

  const handleSave = async (provider: string, secret: string) => {
    await saveCred.mutateAsync({
      provider,
      label: `${provider} key`,
      secret,
    });
  };

  const handleTest = async (provider: string, secret: string) => {
    return validateCred.mutateAsync({ provider, secret });
  };

  const handleRevoke = async (provider: string) => {
    const cred = findCredential(credentials, provider);
    if (!cred) return;
    await revokeCred.mutateAsync(cred.id);
  };

  const handleRotate = async (provider: string) => {
    const cred = findCredential(credentials, provider);
    if (!cred) return;
    await rotateCred.mutateAsync(cred.id);
  };

  const handleToggleProvider = (provider: string, enabled: boolean) => {
    void updatePolicy.mutateAsync({ provider, enabled });
  };

  return (
    <div className="space-y-6">
      {/* Empty state banner */}
      {!credsLoading && !hasAnyConfigured && <EmptyStateBanner />}

      {/* Provider cards */}
      <div className="space-y-4">
        <h2 className="text-sm font-semibold text-stone-700 dark:text-stone-300 uppercase tracking-wide">
          API Keys
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {PROVIDER_CONFIGS.map((config) => {
            const credential = findCredential(credentials, config.id);
            const enabled = isProviderEnabled(policies, config.id);

            return (
              <ProviderCard
                key={config.id}
                config={config}
                credential={credential}
                enabled={enabled}
                onToggleEnabled={(val) => handleToggleProvider(config.id, val)}
                onSave={(secret) => handleSave(config.id, secret)}
                onTest={(secret) => handleTest(config.id, secret)}
                onRotate={() => handleRotate(config.id)}
                onRevoke={() => handleRevoke(config.id)}
                isSaving={saveCred.isPending}
                isTesting={validateCred.isPending}
                isRevoking={revokeCred.isPending}
                isRotating={rotateCred.isPending}
              />
            );
          })}
        </div>
      </div>

      {/* Which model handles which task/tier lives on the Integrations page,
          not here — this tab only manages provider keys and their on/off
          policy. Pointed here explicitly because a fully-configured but
          non-functional "Task Model Assignments" table used to live in this
          spot (removed 2026-09-25: it was local-only state with no save
          path, and modeled task→specific-model instead of the real
          task→tier mechanism Settings actually uses). */}
      <p className="text-xs text-stone-500 dark:text-stone-400">
        Looking to change which model handles resume evaluation, quality audits, or other
        tasks? That's configured under Settings → Integrations → AI Configuration.
      </p>
    </div>
  );
}
